import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  forwardRef,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  Validator,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { CountryCode } from 'libphonenumber-js';
import {
  buildCountryOptions,
  CountryPhoneOption,
  getNationalInputMaxLength,
  getNationalLength,
  getPhoneLimitError,
  parseExistingPhone,
  validateNationalPhone,
} from './country-phone.util';

@Component({
  selector: 'app-phone-input',
  templateUrl: './phone-input.component.html',
  styleUrls: ['./phone-input.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneNumberInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PhoneNumberInputComponent),
      multi: true,
    },
  ],
})
export class PhoneNumberInputComponent implements OnInit, OnChanges, ControlValueAccessor, Validator {
  @Input() placeholder = 'Enter phone number';
  @Input() initialCountry: CountryCode = 'PK';
  @Input() phoneNumber = '';
  @Input() required = true;
  @Input() disabled = false;

  @Output() phoneNumberChange = new EventEmitter<string>();
  @Output() validNumber = new EventEmitter<string>();
  @Output() formattedForSaving = new EventEmitter<string>();
  @Output() isValidNumber = new EventEmitter<boolean>();

  countryOptions: CountryPhoneOption[] = buildCountryOptions();
  selectedCountry: CountryCode = 'PK';
  nationalInput = '';
  invalidNumber = false;
  invalidMessage = '';
  nationalMaxLength = 11;
  nationalLimit = 10;

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};
  private onValidatorChange: () => void = () => {};
  private lastEmittedE164 = '';

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit() {
    this.selectedCountry = this.initialCountry || 'PK';
    this.refreshLimit();
    if (this.phoneNumber) {
      this.applyIncomingValue(this.phoneNumber);
    } else {
      this.validateNumber(this.selectedCountry, this.nationalInput);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
  if (changes['phoneNumber'] && !changes['phoneNumber'].firstChange) {
    const incoming = this.phoneNumber || '';

    if (!incoming) {
      this.applyIncomingValue('');
    } else if (
      incoming !== this.lastEmittedE164 &&
      incoming !== this.nationalInput
    ) {
      this.applyIncomingValue(incoming);
    }
  }

  if (changes['initialCountry'] && !this.phoneNumber && !this.nationalInput) {
    this.selectedCountry = this.initialCountry || 'PK';
    this.refreshLimit();
  }
}

  writeValue(value: string): void {
    this.applyIncomingValue(value || '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.detectChanges();
  }

  validate(_control: AbstractControl): ValidationErrors | null {
    if (!this.required && !this.nationalInput) {
      return null;
    }
    if (this.invalidNumber) {
      return {
        phoneInvalid: {
          requiredLength: this.nationalLimit,
          message: this.invalidMessage,
        },
      };
    }
    return null;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  onCountryCodeChange(country: CountryCode) {
    this.selectedCountry = country;
    this.refreshLimit();
    if (this.nationalInput.length > this.nationalMaxLength) {
      this.nationalInput = this.nationalInput.slice(0, this.nationalMaxLength);
    }
    this.validateNumber(this.selectedCountry, this.nationalInput, false);
    this.onTouched();
  }

  onPhoneNumberChange(value: string) {
    this.applyTypedOrPastedValue(value);
    this.onTouched();
  }

  allowOnlyDigits(event: KeyboardEvent) {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  onPasteDigits(event: ClipboardEvent) {
    event.preventDefault();
    const pastedRaw = (event.clipboardData?.getData('text') || '').trim();
    const input = event.target as HTMLInputElement;
    const start = input.selectionStart ?? this.nationalInput.length;
    const end = input.selectionEnd ?? this.nationalInput.length;
    const replacingAll = start === 0 && end === (this.nationalInput || '').length;
    const isIntlPaste = pastedRaw.startsWith('+') || pastedRaw.startsWith('00');

    // Full number with country code: detect country and keep only national digits.
    if (isIntlPaste || replacingAll || !this.nationalInput) {
      this.applyIncomingValue(pastedRaw, true);
      this.onTouched();
      return;
    }

    const pasted = pastedRaw.replace(/\D/g, '');
    const merged =
      this.nationalInput.slice(0, start) + pasted + this.nationalInput.slice(end);
    this.applyTypedOrPastedValue(merged);
    this.onTouched();
  }

  getSelectedCountryDisplay(): string {
    const option = this.countryOptions.find((item) => item.value === this.selectedCountry);
    if (!option) {
      return this.selectedCountry || '';
    }
    return `${option.name} ${option.dial}`;
  }

  private applyIncomingValue(value: string, lockCountry = false) {
    if (!value) {
      this.nationalInput = '';
      if (!lockCountry) {
        this.selectedCountry = this.initialCountry || 'PK';
      }
      this.refreshLimit();
      this.validateNumber(this.selectedCountry, this.nationalInput);
      return;
    }
    const selected = this.selectedCountry || this.initialCountry || 'PK';
    const parsed = parseExistingPhone(value, selected, this.initialCountry || 'PK');
    if (!lockCountry) {
      this.selectedCountry = parsed.countryIso;
    }
    this.nationalInput = (parsed.nationalDigits || '').slice(0, 15);
    this.refreshLimit();
    this.nationalMaxLength = Math.max(this.nationalMaxLength, this.nationalInput.length);
    this.validateNumber(this.selectedCountry, this.nationalInput);
  }

  /** Typing stays national digits; longer/intl values strip the detected country code first. */
  private applyTypedOrPastedValue(value: string) {
    const raw = String(value || '').trim();
    if (!raw) {
      this.nationalInput = '';
      this.refreshLimit();
      this.validateNumber(this.selectedCountry, this.nationalInput);
      return;
    }
    if (raw.startsWith('+') || raw.startsWith('00')) {
      this.applyIncomingValue(raw, true);
      return;
    }
    const digits = raw.replace(/\D/g, '');
    const fallbackIso = this.selectedCountry || this.initialCountry || 'PK';
    if (digits.length > getNationalInputMaxLength(fallbackIso, digits)) {
      this.applyIncomingValue(digits, true);
      return;
    }
    this.nationalInput = digits;
    this.refreshLimit();
    this.nationalInput = this.nationalInput.slice(0, this.nationalMaxLength);
    this.validateNumber(this.selectedCountry, this.nationalInput);
  }

  private refreshLimit() {
    this.nationalLimit = getNationalLength(this.selectedCountry);
    this.nationalMaxLength = getNationalInputMaxLength(this.selectedCountry, this.nationalInput);
  }

  private validateNumber(country: CountryCode, number: string, allowCountrySwitch = true) {
    const result = validateNationalPhone(
      number,
      country,
      this.required,
      this.initialCountry || 'PK',
      allowCountrySwitch
    );
    this.invalidNumber = !result.valid;
    this.invalidMessage = result.error || getPhoneLimitError(this.selectedCountry, false, number);

    if (result.valid) {
      const e164 = result.e164 || number;
      this.lastEmittedE164 = e164;
      this.formattedForSaving.emit(e164);
      this.validNumber.emit(e164);
      this.phoneNumberChange.emit(e164);
      this.onChange(e164);
      this.isValidNumber.emit(true);
    } else {
      this.lastEmittedE164 = '';
      this.phoneNumberChange.emit(number);
      this.onChange(number);
      this.isValidNumber.emit(false);
    }
    this.onValidatorChange();
    this.cdr.detectChanges();
  }
}
